import React from 'react';
import Page from './page';

function PeticleTerms() {
    return (
        <Page label="Peticle — Legal" title="Terms of Use">
            <div className="doc">
                Effective Date: 17 May 2025
                <br /><br />
                Welcome to Peticle, a pet care app created by Unit 326. By downloading, installing, or using Peticle,
                you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the
                app.
                <br /><br />

                <h3>Interpretation and Definitions</h3><br />

                <h5>Interpretation</h5>
                <p>The words of which the initial letter is capitalized have meanings defined under the following
                    conditions. The following definitions shall have the same meaning regardless of whether they appear
                    in singular or in plural.</p>

                <h6>Definitions</h6>
                <p>For the purposes of these Terms of Use:</p>
                <ul>
                    <li>
                        <p><strong>Account</strong> means a unique account created for You to access our Service or
                            parts of our Service.</p>
                    </li>
                    <li>
                        <p><strong>Application</strong> refers to Peticle, the software program provided by the
                            Company.</p>
                    </li>
                    <li>
                        <p><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this
                            Agreement) refers to Unit 326, Joburg, South Africa.</p>
                    </li>
                    <li>
                        <p><strong>Content</strong> means any text, images, data, or other material You submit to or
                            generate through the Service, including Pet Data.</p>
                    </li>
                    <li>
                        <p><strong>Country</strong> refers to: South Africa</p>
                    </li>
                    <li>
                        <p><strong>Device</strong> means any device that can access the Service such as a computer, a
                            cellphone or a digital tablet.</p>
                    </li>
                    <li>
                        <p><strong>Pet Data</strong> means any images, health information, breed data, or other
                            information relating to Your pet that You upload or that is generated through use of the
                            Service.</p>
                    </li>
                    <li>
                        <p><strong>Service</strong> refers to the Application.</p>
                    </li>
                    <li>
                        <p><strong>You</strong> means the individual accessing or using the Service, or the company, or
                            other legal entity on behalf of which such individual is accessing or using the Service, as
                            applicable.</p>
                    </li>
                </ul>

                <br /><br />

                <h3>1. Acceptance of Terms</h3><br />
                <p>By creating an Account or using the Service, You confirm that You are at least 13 years of age, that
                    You have read and understood these Terms of Use, and that You agree to be legally bound by them. If
                    You are using the Service on behalf of an organisation, You represent that You have the authority to
                    bind that organisation to these terms.</p>

                <br />

                <h3>2. Description of Service</h3><br />
                <p>Peticle is a pet care application that provides the following features:</p>
                <ul>
                    <li>AI-powered breed identification from photos</li>
                    <li>Visual health analysis of pet images (wound assessment, coat, eyes, ears, and other areas)</li>
                    <li>Pet health record management including vet visits, vaccinations, and medical history</li>
                    <li>Care reminders for appointments, deworming, flea treatment, and other scheduled events</li>
                </ul>
                <p>We reserve the right to modify, suspend, or discontinue any feature of the Service at any time
                    without prior notice.</p>

                <br />

                <h3>3. User Accounts</h3><br />
                <p>To access certain features of the Service, You must create an Account. You agree to:</p>
                <ul>
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain the confidentiality of Your account credentials</li>
                    <li>Notify Us immediately at <a href="mailto:hello@unit326.com">hello@unit326.com</a> if You
                        suspect unauthorised access to Your Account</li>
                    <li>Accept responsibility for all activity that occurs under Your Account</li>
                </ul>
                <p>We reserve the right to suspend or terminate Accounts that violate these Terms.</p>

                <br />

                <h3>4. Acceptable Use</h3><br />
                <p>You agree to use Peticle only for lawful purposes and in a manner consistent with these Terms. You
                    must not:</p>
                <ul>
                    <li>Upload Content that is false, misleading, or fraudulent</li>
                    <li>Use the Service to harm, abuse, or harass others</li>
                    <li>Attempt to reverse-engineer, decompile, or tamper with the Application</li>
                    <li>Use automated tools, bots, or scrapers to access the Service without our prior written
                        consent</li>
                    <li>Circumvent or attempt to bypass any security or access controls in the Service</li>
                    <li>Upload content that infringes the intellectual property rights of any third party</li>
                </ul>

                <br />

                <h3>5. Medical Analysis Disclaimer</h3><br />
                <p>The medical analysis features in Peticle — including wound assessment, coat condition analysis, eye
                    and ear health checks, and other visual health evaluations — are powered by AI and computer vision
                    technology. These features are provided for <strong>informational and educational purposes
                    only</strong> and do <strong>not</strong> constitute professional veterinary advice, diagnosis,
                    or treatment.</p>
                <p>Results generated by Peticle's analysis tools:</p>
                <ul>
                    <li>Are not a substitute for a professional veterinary examination</li>
                    <li>May not be accurate in all circumstances or for all animals</li>
                    <li>Should not be used as the sole basis for making decisions about Your pet's health or care</li>
                </ul>
                <p>Always consult a qualified veterinarian for any concerns about Your pet's health. Do not delay
                    seeking professional veterinary care based on results provided by the app. In an emergency, contact
                    a veterinarian or emergency animal clinic immediately.</p>
                <p>By using these features, You acknowledge and accept that You use them at Your own risk and that
                    the Company is not liable for any outcomes resulting from reliance on AI-generated health
                    assessments.</p>

                <br />

                <h3>6. Your Content</h3><br />
                <p>You retain ownership of all Content You submit to the Service. By uploading Content, You grant Us a
                    non-exclusive, worldwide, royalty-free licence to use, store, and process that Content solely for
                    the purpose of providing and improving the Service, in accordance with our <a
                    href="/peticle-policy">Privacy Policy</a>.</p>
                <p>You represent and warrant that You have all necessary rights to the Content You submit, and that
                    your Content does not violate any applicable law or the rights of any third party.</p>

                <br />

                <h3>7. Intellectual Property</h3><br />
                <p>All intellectual property rights in the Service — including the app design, software, trademarks,
                    logos, and AI models — are owned by or licensed to the Company. Nothing in these Terms grants You
                    any rights in or to the Service or its underlying technology beyond the limited right to use the
                    Service as described herein.</p>

                <br />

                <h3>8. Third-Party Services</h3><br />
                <p>Peticle may allow You to sign in using Gmail or Apple account credentials, and may integrate with
                    third-party AI or cloud service providers to deliver features. Your use of such third-party services
                    is subject to their respective terms and privacy policies. We are not responsible for the conduct,
                    content, or policies of any third-party service.</p>

                <br />

                <h3>9. Disclaimer of Warranties</h3><br />
                <p>The Service is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis
                    without warranties of any kind, whether express or implied, including but not limited to warranties
                    of merchantability, fitness for a particular purpose, or non-infringement.</p>
                <p>We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other
                    harmful components.</p>

                <br />

                <h3>10. Limitation of Liability</h3><br />
                <p>To the maximum extent permitted by applicable law, the Company and its affiliates, officers,
                    employees, and agents shall not be liable for any indirect, incidental, special, consequential, or
                    punitive damages, including but not limited to loss of data, loss of profits, or harm to Your pet
                    arising out of or in connection with Your use of the Service or reliance on any information
                    provided by the Service.</p>
                <p>Our total liability to You for any claim arising from or relating to the Service shall not exceed
                    the amount You paid to Us (if any) in the twelve months preceding the claim.</p>

                <br />

                <h3>11. Termination</h3><br />
                <p>We may suspend or terminate Your access to the Service at any time, with or without notice, if We
                    reasonably believe You have violated these Terms or if required by law. You may delete Your Account
                    at any time through the app's account settings. Upon termination, Your right to use the Service
                    ceases immediately and We will handle Your data in accordance with our <a
                    href="/peticle-policy">Privacy Policy</a>.</p>

                <br />

                <h3>12. Changes to These Terms</h3><br />
                <p>We reserve the right to update or modify these Terms at any time. Changes will be effective upon
                    posting within the app or on our website. We encourage You to review these Terms periodically.
                    Continued use of the Service after changes are posted constitutes Your acceptance of the revised
                    Terms.</p>

                <br />

                <h3>13. Governing Law</h3><br />
                <p>These Terms are governed by and construed in accordance with the laws of the Republic of South
                    Africa. Any disputes arising from these Terms or Your use of the Service shall be subject to the
                    exclusive jurisdiction of the courts of South Africa.</p>

                <br />

                <h3>14. Contact Us</h3><br />
                <p>If you have any questions or concerns about these Terms of Use, please contact us at:</p>
                <p>
                    <strong>Unit 326</strong><br />
                    Email: <a href="mailto:hello@unit326.com">hello@unit326.com</a><br />
                    Website: <a href="https://unit326.com">unit326.com</a>
                </p>

                <br />
                Thank you for using Peticle and trusting us with the care of your pets.
                <br /><br />
                Unit 326
            </div>
        </Page>
    );
}

export default PeticleTerms;
